import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * GenderMorph — "The Magic Letter ة" masculine→feminine transformer.
 *
 * Each row shows a masculine word with a pulsing "+ ة" button; tapping it
 * reveals the feminine form with the tāʾ marbūṭa ending highlighted, and
 * plays it. Completes when every pair has been transformed. Also reused for
 * the adjective teaser (same rule, adjective pairs).
 */
function FemWord({ femAr }) {
  // Color everything from the ة onward (the ة plus any tanwīn riding on it).
  const i = femAr.lastIndexOf("ة");
  if (i === -1) return femAr;
  return (
    <>
      {femAr.slice(0, i)}
      <span style={{ color: "var(--tag-violet)" }}>{femAr.slice(i)}</span>
    </>
  );
}

export default function GenderMorph({ data, onComplete, audio }) {
  const [morphedIds, setMorphedIds] = useState(() => new Set());

  const pairs = data.pairs;
  const unitLabel = data.unit_label || "words";
  const allMorphed = morphedIds.size >= pairs.length;

  useEffect(() => {
    if (allMorphed) onComplete();
  }, [allMorphed, onComplete]);

  const handleMorph = (pair) => {
    if (morphedIds.has(pair.id)) return;
    setMorphedIds((prev) => new Set(prev).add(pair.id));
    audio.play(pair.fem_ar);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{morphedIds.size}/{pairs.length} {unitLabel} transformed</ProgressPill>
      </div>

      <div className="flex flex-col gap-3">
        {pairs.map((pair) => {
          const morphed = morphedIds.has(pair.id);
          return (
            <div
              key={pair.id}
              className="rounded-xl px-4 py-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
            >
              <div className="text-center">
                <p dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.8] m-0" style={{ color: "var(--parchment)" }}>
                  {pair.masc_ar}
                </p>
                <p className="text-xs mt-1 mb-0 inline-flex items-center gap-2" style={{ color: "var(--parchment-dim)" }}>
                  {pair.masc_en}
                  {audio.audioAllowed && (
                    <PlayButton text={pair.masc_ar} playingText={audio.playingText} onPlay={audio.play} />
                  )}
                </p>
              </div>

              <div className="flex justify-center">
                {morphed ? (
                  <span className="text-xl" aria-hidden="true" style={{ color: "var(--gold-dim)" }}>→</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleMorph(pair)}
                    className="rounded-lg px-3 pt-4 pb-1.5 font-arabic text-2xl leading-[1.4] transition-all hover:brightness-125 animate-pulse font-semibold"
                    style={{
                      color: "var(--gold-bright)",
                      background: "var(--gold-bg)",
                      border: "1px dashed var(--gold-dim)",
                    }}
                    title="Tap to add the ة and make it feminine"
                  >
                    {data.button_label || "+ ة"}
                  </button>
                )}
              </div>

              <div className="text-center">
                {morphed ? (
                  <>
                    <p dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.8] m-0" style={{ color: "var(--parchment)" }}>
                      <FemWord femAr={pair.fem_ar} />
                    </p>
                    <p className="text-xs mt-1 mb-0 inline-flex items-center gap-2" style={{ color: "var(--parchment-dim)" }}>
                      {pair.fem_en}
                      {audio.audioAllowed && (
                        <PlayButton text={pair.fem_ar} playingText={audio.playingText} onPlay={audio.play} />
                      )}
                    </p>
                  </>
                ) : (
                  <p className="font-arabic text-3xl m-0" aria-hidden="true" style={{ color: "var(--parchment-dim)", opacity: 0.35 }}>
                    ؟
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {allMorphed && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
